import { useEffect, useRef } from 'react'
import RightSideBarInfo from '@/components/ui/RightSideBarInfo'
import { basicEnrichment, interestConflictEnrichment } from './constants'

export const depthLevelKey = 'nivel-de-profundidad'

interface ProcessInfoPanelProps {
  open: boolean
  onClose: () => void
  processKey: string | null
}

function BasicEnrichmentContent() {
  return (
    <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
      <p>
        A partir de uno o más <strong>RUTs buscados</strong> (personas o empresas), el proceso
        consulta fuentes disponibles y genera un <strong>reporte tabular</strong> con antecedentes y
        relaciones de <strong>primer nivel</strong>.
      </p>

      <div>
        <h4 className="font-semibold text-gray-900 mb-2">Información incluida</h4>
        <ul className="space-y-1.5 list-disc pl-4">
          <li>
            <strong>Empresas</strong>: razón social, datos comerciales y tributarios.
          </li>
          <li>
            <strong>Personas naturales</strong>: nombre completo, ubicación y atributos disponibles.
          </li>
          <li>
            <strong>Direcciones</strong> vinculadas al RUT.
          </li>
          <li>
            <strong>Relaciones societarias</strong>: personas jurídicas (socios/participaciones).
          </li>
          <li>
            <strong>Relaciones familiares</strong>: personas naturales (parentesco cuando exista).
          </li>
          <li>
            <strong>Actividades económicas</strong> asociadas.
          </li>
          <li>
            <strong>PEP</strong>: coincidencias y vínculos con personas políticamente expuestas.
          </li>
        </ul>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <h4 className="font-semibold text-gray-900 mb-1">Descarga</h4>
        <p>
          <strong>Excel</strong> con múltiples hojas (antecedentes + relaciones), listo para
          análisis.
        </p>
      </div>
    </div>
  )
}

function InterestConflictContent() {
  return (
    <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
      <p>
        Compara dos listas de RUTs — <strong>lista A</strong> (colaboradores) y{' '}
        <strong>lista B</strong> (proveedores) — para detectar vínculos que representen un{' '}
        <strong>conflicto de interés</strong>.
      </p>

      <div>
        <h4 className="font-semibold text-gray-900 mb-2">¿Cómo detectamos un conflicto?</h4>
        <p>
          Para cada RUT de la <strong>lista B</strong>, el algoritmo construye un grafo de relaciones
          explorando vínculos familiares y societarios. Si en cualquier punto aparece un RUT de la{' '}
          <strong>lista A</strong>, se marca como <strong>conflicto de interés</strong>.
        </p>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-2">Reglas de exploración</h4>

        <div className="mb-3">
          <p className="font-medium text-gray-800 mb-1 underline">Si el nodo es una persona:</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>
              Identifica <strong>familiares</strong> y su parentesco.
            </li>
            <li>
              Busca <strong>empresas</strong> donde la persona o sus familiares participan.
            </li>
            <li>
              Explora <strong>socios</strong> y conexiones societarias dentro del límite configurado.
            </li>
          </ol>
        </div>

        <div>
          <p className="font-medium text-gray-800 mb-1 underline">Si el nodo es una empresa:</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>
              Identifica <strong>socios</strong> (personas o empresas).
            </li>
            <li>
              Explora relaciones societarias y familiares de esos socios dentro del límite.
            </li>
          </ol>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <h4 className="font-semibold text-gray-900 mb-1">Descarga</h4>
        <p>
          <strong>PowerPoint</strong> con una lámina por conflicto encontrado, incluyendo
          visualización del grafo de relaciones y descripción detallada.
        </p>
        <p>
          <strong>Excel</strong>: tabla con el detalle de cada conflicto detectado (ruta de vínculos,
          profundidad, colaboradores y proveedores involucrados). Útil para auditorías y revisión
          masiva.
        </p>
      </div>
    </div>
  )
}

function DepthLevelContent() {
  return (
    <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
      <p>
        El algoritmo parte de un RUT (persona o empresa) de la <strong>lista B</strong> y sigue las
        siguientes reglas para construir un árbol de relaciones:
      </p>

      <div>
        <p className="font-medium text-gray-800 mb-1 underline">Si es una persona:</p>
        <ol className="list-decimal pl-4 space-y-1">
          <li>
            Identifica a sus <strong>familiares</strong> (hasta 3 niveles).
          </li>
          <li>
            Busca las <strong>empresas</strong> donde ella o sus familiares son socios (profundidad
            +1).
          </li>
          <li>
            Explora otros <strong>socios</strong> (personas o empresas) y sus relaciones societarias
            (hasta 3 niveles).
          </li>
          <li>
            Luego, busca los <strong>familiares</strong> de esos socios (hasta 3 niveles).
          </li>
        </ol>
      </div>

      <div>
        <p className="font-medium text-gray-800 mb-1 underline">Si es una empresa:</p>
        <ol className="list-decimal pl-4 space-y-1">
          <li>
            Identifica a sus <strong>socios</strong> (personas o empresas) y sus relaciones
            societarias (hasta 3 niveles).
          </li>
          <li>
            Luego, busca los <strong>familiares</strong> de esos socios (hasta 3 niveles).
          </li>
        </ol>
      </div>

      <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
        <p className="text-xs text-gray-600">
          Se genera un árbol de hasta <strong>10 niveles de profundidad</strong> y, si algún RUT de
          la <strong>lista A</strong> aparece, se considera un <strong>conflicto de interés</strong>.
        </p>
      </div>
    </div>
  )
}

const PANEL_CONFIG: Record<string, { title: string; description: string }> = {
  [basicEnrichment]: {
    title: 'Enriquecimiento básico',
    description: 'Detalle del tipo de proceso',
  },
  [interestConflictEnrichment]: {
    title: 'Conflicto de interés',
    description: 'Detalle del tipo de proceso',
  },
  [depthLevelKey]: {
    title: 'Nivel de profundidad',
    description: 'Cómo se exploran las relaciones',
  },
}

export default function ProcessInfoPanel({ open, onClose, processKey }: ProcessInfoPanelProps) {
  // Store the last valid processKey to keep the component mounted during close animation
  const lastValidKeyRef = useRef<string | null>(null)

  // Update lastValidKey only when we have a valid processKey
  useEffect(() => {
    if (processKey) {
      lastValidKeyRef.current = processKey
    }
  }, [processKey])

  // Use the current processKey if available, otherwise use the last valid one
  const activeKey = processKey || lastValidKeyRef.current
  
  if (!activeKey) return null

  const config = PANEL_CONFIG[activeKey]
  if (!config) return null

  return (
    <RightSideBarInfo
      open={open}
      onClose={onClose}
      title={config.title}
      description={config.description}
    >
      {activeKey === basicEnrichment && <BasicEnrichmentContent />}
      {activeKey === interestConflictEnrichment && <InterestConflictContent />}
      {activeKey === depthLevelKey && <DepthLevelContent />}
    </RightSideBarInfo>
  )
}
